// pages/tasks/InlineStatusPicker.jsx
//
// Click-to-change status directly from a table row (Jira-style), reusing
// the EXACT SAME status workflow API TaskDetailPage.jsx's "Change Status"
// panel already uses -- GET /tasks/:id/allowed-transitions to list the
// legal next states for THIS task, POST /tasks/:id/transitions/:transition_id
// to execute one (with a remark when the transition requires it). Never a
// second status-change contract, and never a hardcoded status list -- the
// available options are whatever the backend's workflow engine allows for
// this task right now.
import React, { useEffect, useRef, useState } from "react";
import api from "../../api";
import { StatusPill } from "./StatusChips";
import "./InlineStatusPicker.css";

export default function InlineStatusPicker({ row, onChanged }) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [transitions, setTransitions] = useState([]);
  const [pending, setPending] = useState(null); // transition awaiting remark confirm
  const [remark, setRemark] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setPending(null);
        setError("");
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  const close = () => {
    setOpen(false);
    setPending(null);
    setRemark("");
    setError("");
  };

  const openPicker = async () => {
    // Flip the popover upward ONLY when there's genuinely not enough room
    // below it AND more room above than below -- comparing solely against
    // "distance to the bottom of the viewport" (the old check) misfires on
    // a row near the TOP of a short window, flipping the popover up into
    // the toolbar/header above the table instead of leaving it below the
    // row where there's actually plenty of space.
    const rect = wrapRef.current?.getBoundingClientRect();
    if (rect) {
      const estimatedPopoverHeight = 260;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setOpenUp(spaceBelow < estimatedPopoverHeight && spaceAbove > spaceBelow);
    } else {
      setOpenUp(false);
    }
    setOpen(true);
    setPending(null);
    setError("");
    setLoading(true);
    try {
      const { data } = await api.get(`/tasks/${row.id}/allowed-transitions`);
      setTransitions(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      setTransitions([]);
      setError(err?.response?.data?.message || "Unable to load status options.");
    } finally {
      setLoading(false);
    }
  };

  const execute = async (transition, remarkText) => {
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/tasks/${row.id}/transitions/${transition.transition_id}`, {
        remark: remarkText.trim() || undefined,
      });
      close();
      onChanged?.();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.code === "TASK_TRANSITION_STALE") {
        setError("Status changed since this was opened — refreshing…");
        onChanged?.();
        setOpen(false);
      } else {
        setError(data?.message || "Failed to update status.");
        setSubmitting(false);
      }
      return;
    }
    setSubmitting(false);
  };

  const pickTransition = (t) => {
    if (t.requires_remark) {
      setPending(t);
      setRemark("");
      setError("");
    } else {
      execute(t, "");
    }
  };

  const confirmRemark = () => {
    if (!remark.trim()) {
      setError("A remark is required for this status change.");
      return;
    }
    execute(pending, remark);
  };

  return (
    <div className="isp-wrap" ref={wrapRef}>
      <button
        type="button"
        className="isp-trigger"
        onClick={() => (open ? close() : openPicker())}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Change status"
      >
        <StatusPill status={row.status} />
        <span className={`isp-chevron${open ? " open" : ""}`}>⌄</span>
      </button>

      {open && (
        <div className={`isp-popover${openUp ? " isp-popover-up" : ""}`}>
          {pending ? (
            <div className="isp-remark-box">
              <div className="isp-popover-head">
                <button type="button" className="isp-back-btn" disabled={submitting} onClick={() => { setPending(null); setError(""); }} aria-label="Back">‹</button>
                <span>Move to <strong>{pending.to_status?.name}</strong></span>
              </div>
              <textarea
                autoFocus
                rows={3}
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="Add a remark…"
              />
              {error && <div className="isp-error">{error}</div>}
              <div className="isp-remark-actions">
                <button type="button" className="isp-btn-ghost" disabled={submitting} onClick={() => { setPending(null); setError(""); }}>
                  Cancel
                </button>
                <button type="button" className="isp-btn-primary" disabled={submitting} onClick={confirmRemark}>
                  {submitting ? "Saving…" : "Confirm"}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="isp-popover-head"><span>Change Status</span></div>
              {loading ? (
                <div className="isp-state">Loading…</div>
              ) : error ? (
                <div className="isp-error isp-state">{error}</div>
              ) : transitions.length ? (
                <ul className="isp-list" role="listbox">
                  {transitions.map((t) => (
                    <li key={t.transition_id}>
                      <button type="button" className="isp-option" disabled={submitting} onClick={() => pickTransition(t)}>
                        <span className="isp-option-name">{t.name}</span>
                        <span className="isp-option-target">
                          <StatusPill status={t.to_status?.code || t.to_status?.name} />
                          {t.requires_remark && <span className="isp-remark-flag" title="Requires a remark">✎</span>}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="isp-state">No status changes available right now.</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
